import { useCallback, useRef } from 'react';

type ManipulationRenderer = {
    selectElement(elementId: number, useMultiple?: boolean): void;
    selectElements(elementIds: number[]): void;
    selectElementsExact(elementIds: number[]): void;
    getSelectedElements(): { id: number }[];
};

/**
 * Given a flat ordered list of element IDs, an anchor ID, and a target ID,
 * returns all IDs in the range [anchor..target] (inclusive, in list order).
 * If anchor or target are not in the list, returns just the target.
 */
export const getShiftClickRange = (flatIds: number[], anchorId: number | null, targetId: number): number[] => {
    if (anchorId === null) {
        return [targetId];
    }

    const anchorIndex = flatIds.indexOf(anchorId);
    const targetIndex = flatIds.indexOf(targetId);

    if (anchorIndex === -1 || targetIndex === -1) {
        return [targetId];
    }

    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);

    return flatIds.slice(start, end + 1);
};

/**
 * Flattens timeline track items (which may have nested children) into element IDs.
 * Always recurses into children regardless of expansion state.
 * Sorts same-renderOrder siblings by startFrame to preserve left-to-right timeline order,
 * so shift-click range works like text selection across tracks.
 */
export const flattenTrackItemIds = (
    items: { element: { id: number; startFrame?: number; renderOrder?: number }; children?: any[] }[],
): number[] => {
    const result: number[] = [];

    // Sort items by startFrame for same-renderOrder items (same track, left-to-right)
    const sorted = [...items].sort((a, b) => {
        const roA = a.element.renderOrder ?? 0;
        const roB = b.element.renderOrder ?? 0;

        if (roA !== roB) return 0; // preserve existing renderOrder sort

        return (a.element.startFrame ?? 0) - (b.element.startFrame ?? 0);
    });

    for (const item of sorted) {
        result.push(item.element.id);

        if (item.children) {
            result.push(...flattenTrackItemIds(item.children));
        }
    }

    return result;
};

export const useShiftSelection = (manipulationRenderer: ManipulationRenderer | null) => {
    const anchorIdRef = useRef<number | null>(null);

    const handleShiftClick = useCallback(
        (targetId: number, flatIds: number[]) => {
            if (!manipulationRenderer) {
                return;
            }

            const rangeIds = getShiftClickRange(flatIds, anchorIdRef.current, targetId);

            // Select exactly the elements in range without recursive group expansion
            manipulationRenderer.selectElementsExact(rangeIds);
        },
        [manipulationRenderer],
    );

    const setAnchor = useCallback((elementId: number) => {
        anchorIdRef.current = elementId;
    }, []);

    return { anchorIdRef, handleShiftClick, setAnchor };
};
